// src/features/chat/models/chatModel.js
/*
 * Legacy compatibility:
 * - Handles legacy message types (search_results/draft_post/post) no longer emitted by Genkit.
 * - Retained only for backward compatibility with existing data.
 * - Remove after legacy data migration/cleanup is complete.
 */

import { auth } from '../../../firebase';

/**
 * Normalize image objects to a consistent shape.
 * @param {Array} images - Raw images array from Firestore
 * @returns {Array} Normalized images with url, name, mimeType
 */
export function normalizeImages(images) {
  if (!Array.isArray(images)) return [];
  return images
    .map((img) => {
      if (!img?.url) return null;
      return {
        url: img.url,
        name: img.name || null,
        mimeType: img.mimeType || img.type || null,
      };
    })
    .filter(Boolean);
}

/**
 * Get milliseconds from a timestamp-like value.
 * @param {*} ts - Firestore Timestamp, Date, or null
 * @returns {number} Milliseconds or 0
 */
export function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (ts instanceof Date) return ts.getTime();
  return 0;
}

/**
 * Normalize a raw Firestore message to a unified shape.
 *
 * Handles schema differences:
 * - Private: sender: 'user'|'assistant', type: 'chat'
 * - Group: senderId: uid|'assistant', type: 'user'|'bot'|'assistant'|'record_draft'|...
 *
 * @param {Object} raw - Raw message from Firestore (with id injected)
 * @param {Object} options
 * @param {import('./chatTarget').ChatTarget} options.target - Chat target for context
 * @returns {Object} Normalized message
 */
export function normalizeMessage(raw, { target }) {
  const uid = auth.currentUser?.uid || null;

  // Handle timestamp field - prefer server timestamp, fall back to client
  const ts = raw.timestamp || raw.createdAt || raw.createdAtClient || null;
  const tsMs = toMillis(ts);

  // Derive senderId across schemas:
  // - Private uses: sender: 'user'|'assistant'|'bot'
  // - Group uses: senderId: uid|'assistant'|'bot'
  let senderId;
  if (raw.senderId) {
    senderId = raw.senderId;
  } else if (target.kind === 'private') {
    senderId = raw.sender === 'user' ? uid : 'assistant';
  } else {
    senderId = raw.sender || null;
  }

  // Derive role from various indicators
  let role;
  if (raw.role) {
    role = raw.role;
  } else if (raw.sender === 'user') {
    role = 'user';
  } else if (senderId === uid) {
    role = 'user';
  } else if (senderId === 'assistant' || senderId === 'bot') {
    role = 'assistant';
  } else if (raw.type === 'user') {
    role = 'user';
  } else if (raw.type === 'bot' || raw.type === 'assistant') {
    role = 'assistant';
  } else {
    role = 'user'; // Default for group messages from other users
  }

  // Unify type to uiType
  // Group uses type: 'user'|'bot'|'assistant' for chat messages
  // Private uses type: 'chat'
  // Both may have: 'record_draft'|'post'|'record'|'search_results'|'draft_post'
  // Legacy message types (search_results/draft_post/post) are no longer emitted by Genkit.
  // Retained only for backward compatibility with existing data; remove after legacy data migration/cleanup.
  const chatTypes = ['user', 'bot', 'assistant', 'chat', undefined, null];
  const uiType = chatTypes.includes(raw.type) ? 'chat' : raw.type;

  return {
    id: raw.id,
    uiType,       // 'chat' | 'record_draft' | 'post' | 'record' | 'search_results' | 'draft_post'
    role,         // 'user' | 'assistant'
    senderId,     // uid | 'assistant' | 'bot' | other uid (group)
    text: raw.text || '',
    images: normalizeImages(raw.images),
    recordCitations: Array.isArray(raw.recordCitations) ?
      raw.recordCitations :
      [],
    draftId: raw.draftId || null,
    recordId: raw.recordId || null,
    postIds: raw.postIds || null,
    inReplyTo: raw.inReplyTo || null,
    timestamp: ts,
    tsMs,         // For stable sorting
  };
}

/**
 * Check if a message is from the assistant.
 * @param {Object} message - Normalized message
 * @returns {boolean}
 */
export function isAssistantMessage(message) {
  return (
    message.role === 'assistant' ||
    message.senderId === 'assistant' ||
    message.senderId === 'bot'
  );
}

/**
 * Check if a message is from the current user.
 * @param {Object} message - Normalized message
 * @returns {boolean}
 */
export function isCurrentUserMessage(message) {
  const uid = auth.currentUser?.uid;
  return uid && message.senderId === uid;
}

/**
 * Sort messages ascending by timestamp (oldest first).
 * @param {Object} a - Normalized message
 * @param {Object} b - Normalized message
 * @returns {number}
 */
export function sortMessagesAsc(a, b) {
  // Sort by timestamp, fallback to id for stability
  const diff = (a.tsMs || 0) - (b.tsMs || 0);
  if (diff !== 0) return diff;
  return a.id > b.id ? 1 : -1;
}

const ASSISTANT_ID_PREFIX = 'assistant-';

function getRepliedToUserMessageId(message) {
  if (!message || typeof message !== 'object') return null;
  if (!isAssistantMessage(message)) return null;
  if (typeof message.inReplyTo === 'string' && message.inReplyTo) {
    return message.inReplyTo;
  }
  if (typeof message.id === 'string' && message.id.startsWith(ASSISTANT_ID_PREFIX)) {
    const suffix = message.id.slice(ASSISTANT_ID_PREFIX.length);
    return suffix || null;
  }
  return null;
}

/**
 * Build an index of user message IDs that have persisted assistant replies.
 * @param {Array} messages - Array of normalized messages
 * @returns {Set<string>}
 */
export function buildAssistantReplyIndex(messages) {
  if (!Array.isArray(messages)) return new Set();
  const index = new Set();
  for (const m of messages) {
    const repliedToId = getRepliedToUserMessageId(m);
    if (repliedToId) index.add(repliedToId);
  }
  return index;
}

/**
 * Check if a persisted assistant reply exists for a given user message.
 * @param {Array} messages - Array of normalized messages
 * @param {string} userMessageId - The user message ID to check
 * @returns {boolean}
 */
export function hasPersistedAssistantReply(messages, userMessageId) {
  if (!userMessageId || !Array.isArray(messages)) return false;
  for (const m of messages) {
    if (getRepliedToUserMessageId(m) === userMessageId) return true;
  }
  return false;
}
